import { Api } from '@/Api';
import { defineStore, storeToRefs } from 'pinia';
import { Item, useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useToast } from '@/composables/useToast';
import { useDebug } from '@/composables/useDebug';

export interface VideoMergeState {
  item?: Item;
  position: 'start' | 'end';
  isMerging: boolean;
}

export const useVideoMergeStore = defineStore('videoMergeStore', {
  state: () =>
    ({
      item: undefined,
      position: 'start',
      isMerging: false,
    }) as VideoMergeState,
  actions: {
    setPosition(position: 'start' | 'end') {
      this.position = position;
    },
    async validateUrl(url: string) {
      const { id } = storeToRefs(useViewerItemStore());
      if (!id?.value) {
        throw new Error('No item ID');
      }
      const payload = this.getPayload(id?.value, url);
      return await Api.item.videoEdit.validate(id?.value, payload);
    },
    async merge(url: string) {
      const { error } = useToast();
      const { id } = storeToRefs(useViewerItemStore());
      if (!id?.value) {
        throw new Error('No item ID');
      }
      const payload = this.getPayload(id?.value, url);
      try {
        this.isMerging = true;
        const res = await Api.item.videoEdit.merge(id?.value, payload);
        useViewerItemStore().setItem(res.data?.item);
        return res;
      } catch (err) {
        error('Cannot merge videos');
        throw err;
      } finally {
        this.isMerging = false;
      }
    },
  },
  getters: {
    getPayload: (state: VideoMergeState) => (currentItemId: string, url: string) => {
      let itemId;
      // try to get item ID from URL otherwise use the URL as the item ID
      try {
        itemId = new URL(url).pathname.split('/')[1];
      } catch {
        const { debug } = useDebug();
        debug('Cannot parse URL', url);
        itemId = url;
      }

      const payload = {
        merge: [itemId, currentItemId],
      };

      if (state.position === 'end') {
        payload.merge = payload.merge.reverse();
      }

      return payload;
    },
  },
});
